/**
 * Rewards API — programs, dated rules, the earned summary, and regret.
 *
 * All arithmetic is integer (multiplier_bps / points_millis /
 * point_value_cents_millis) — display strings come from the server or
 * lib/money.ts, never ad-hoc math in components.
 *
 * There is deliberately no `listAccruals` here. `GET /rewards/accruals`
 * returns one synthesized row per program (`id: "summary:<pid>"`,
 * `transaction_id: ""` — service.py:3553) which is the same per-program
 * rollup `GET /rewards/summary` already serves in a purpose-built envelope.
 * Two client paths to one number is the split-brain the tenets forbid; when a
 * genuine per-transaction accrual view is wanted, it needs a route that
 * returns per-transaction rows.
 */

import { apiClient, buildQueryString } from "@/shared/lib/api/client";
import type {
  RegretResponse,
  RewardProgram,
  RewardProgramCreateInput,
  RewardRule,
  RewardRuleCreateInput,
  RewardSummary,
} from "../types";

const BASE = "/api/finance/rewards";

// ===================
// Programs
// ===================

export function listPrograms(): Promise<RewardProgram[]> {
  return apiClient<RewardProgram[]>(`${BASE}/programs`);
}

export function createProgram(
  data: RewardProgramCreateInput,
): Promise<RewardProgram> {
  return apiClient<RewardProgram>(`${BASE}/programs`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

// ===================
// Rules — dated, recomputable
// ===================

export function listRules(programId: string): Promise<RewardRule[]> {
  return apiClient<RewardRule[]>(`${BASE}/programs/${programId}/rules`);
}

export function createRule(data: RewardRuleCreateInput): Promise<RewardRule> {
  return apiClient<RewardRule>(
    `${BASE}/programs/${data.program_id}/rules`,
    {
      method: "POST",
      body: JSON.stringify(data),
    },
  );
}

// ===================
// Summaries
// ===================

export function getRewardSummary(params?: {
  since?: string;
  until?: string;
}): Promise<RewardSummary> {
  const qs = buildQueryString({
    since: params?.since,
    until: params?.until,
  });
  return apiClient<RewardSummary>(`${BASE}/summary${qs}`);
}

/**
 * The killer view: what every OTHER card would have earned.
 *
 * `since` and `until` are REQUIRED — the route declares them as bare
 * `Query()` with no default (router.py:689-690), so omitting either is a 422,
 * not a defaulted window. Typing them required is what stops the caller
 * discovering that in production.
 */
export function getRegret(params: {
  since: string;
  until: string;
}): Promise<RegretResponse> {
  const qs = buildQueryString({
    since: params.since,
    until: params.until,
  });
  return apiClient<RegretResponse>(`${BASE}/regret${qs}`);
}
